import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, Compass, Palette, Zap, Sparkles, ArrowRight, Loader2 } from "lucide-react";

/**
 * WelcomeSetup — the "getting started" card on the Dashboard.
 *
 * Renders only when the user hasn't yet completed all three setup steps
 * (Onboarding → Brand Kit → Strategy Operator). Once every step is done
 * the component returns null so the dashboard stays uncluttered.
 *
 * Fetches `GET /api/setup-progress` on mount. Failure is non-fatal — a
 * missed progress fetch just hides the card, it never breaks the dashboard.
 */
export default function WelcomeSetup({ firstName = "Diver" }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/setup-progress")
      .then(({ data }) => { if (!cancel) setProgress(data); })
      .catch(() => { if (!cancel) setProgress(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <div className="mt-6 sm:mt-8 dms-card p-6 flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-[#1D4E89]" />
        <span className="text-[13.5px] text-[#64748B]">Checking your setup…</span>
      </div>
    );
  }

  // Full setup done — get out of the user's way.
  if (!progress || progress.complete) return null;

  const steps = [
    {
      id: "onboarding", done: progress.onboarded,
      title: "Complete onboarding", icon: Compass, to: "/onboarding",
      blurb: "Answer the guided questions so DMS learns your business, voice and audience. This creates your Dive Brain — the source of truth every tool draws from.",
      cta: progress.onboarded ? "Completed" : "Start onboarding",
    },
    {
      id: "brand-kit", done: progress.brand_kit_configured,
      title: "Set your Brand Kit", icon: Palette, to: "/settings/brand-kit",
      blurb: "Colours, typography and your logo. Set it once and every carousel, slide export and brand-aware output stays visually consistent with your identity.",
      cta: progress.brand_kit_configured ? "Edit brand kit" : "Configure brand kit",
    },
    {
      id: "strategy", done: progress.strategy_ready,
      title: "Activate the Strategy Operator", icon: Zap, to: "/strategy",
      blurb: "Approve your 90-day strategy foundation and generate the calendar. Once active, DMS emails you a ready-to-post prompt every morning so momentum becomes automatic.",
      cta: progress.strategy_ready ? "Manage strategy" : "Set up strategy",
    },
  ];

  return (
    <section
      data-testid="welcome-setup"
      className="mt-6 sm:mt-8 rounded-2xl bg-gradient-to-br from-[#0A2342] via-[#0F2E52] to-[#1D4E89] text-white p-6 sm:p-8 lg:p-10 shadow-xl border border-[#0A2342] overflow-hidden relative"
    >
      <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#4FD1C5]/12 pointer-events-none" />
      <div className="absolute -right-40 -bottom-40 w-96 h-96 rounded-full bg-[#F59E0B]/8 pointer-events-none" />

      <div className="relative">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5] mb-4">
          <Sparkles className="w-3.5 h-3.5" /> Welcome to DMS
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-heading font-bold leading-tight max-w-3xl text-white">
          You're in, {firstName}. Now let's turn DMS into your unfair advantage.
        </h2>
        <p className="mt-4 text-[14.5px] sm:text-[15.5px] leading-relaxed text-white/85 max-w-3xl">
          DMS becomes exponentially more powerful with every piece of context you feed it. To get real, on-brand marketing that sounds like <em>you</em> — not generic AI slop — we strongly recommend you complete these three steps below <strong>thoroughly and honestly</strong>. Rush them and you'll get rushed output. Take an hour, and DMS will pay you back with months of aligned content.
        </p>

        <p className="mt-3 text-[13.5px] text-white/70 max-w-3xl">
          <strong className="text-white/90">Already used ChatGPT, Claude or Gemini to develop your brand?</strong>&nbsp;
          Great — the onboarding includes a ready-to-copy <em>extraction prompt</em>. Paste it into the AI you've already been working with, and it will summarise every relevant detail into a structured document you can upload here in one go. No re-typing, no rework.
        </p>

        {/* Progress bar */}
        <div className="mt-7 max-w-3xl">
          <div className="flex items-center justify-between text-[12px] font-semibold text-white/80 mb-2">
            <span>Setup progress</span>
            <span data-testid="welcome-setup-percentage">{progress.percentage}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div
              data-testid="welcome-setup-progress-bar"
              style={{ width: `${progress.percentage}%` }}
              className="h-full rounded-full bg-gradient-to-r from-[#4FD1C5] to-[#F59E0B] transition-all duration-700"
            />
          </div>
        </div>

        {/* Steps */}
        <div className="mt-8 grid gap-3 sm:gap-4 md:grid-cols-3">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <Link
                key={step.id}
                to={step.to}
                data-testid={`welcome-step-${step.id}`}
                className={`group relative flex flex-col rounded-xl p-5 transition ${
                  step.done
                    ? "bg-white/5 border border-white/15 hover:bg-white/10"
                    : "bg-white/10 border border-white/25 hover:bg-white/15 hover:border-[#4FD1C5]"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    step.done ? "bg-[#4FD1C5]/20 text-[#4FD1C5]" : "bg-[#F59E0B]/20 text-[#F59E0B]"
                  }`}>
                    {step.done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/50">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="text-[15.5px] font-heading font-bold text-white leading-snug">
                  {step.title}
                </h3>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-white/70 flex-1">
                  {step.blurb}
                </p>
                <div className={`mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold ${
                  step.done ? "text-white/70 group-hover:text-white" : "text-[#4FD1C5] group-hover:text-white"
                }`}>
                  {step.cta} <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            );
          })}
        </div>

        <p className="mt-6 text-[12.5px] text-white/60 italic max-w-3xl">
          You can skip around and use any tool immediately — but the tools you use before completing setup will produce noticeably weaker output. This card disappears once all three steps are done.
        </p>
      </div>
    </section>
  );
}
